import { OverlayConfig, OverlayModule, OverlayPositionBuilder, ScrollStrategyOptions } from '@angular/cdk/overlay';
import { PlatformModule } from '@angular/cdk/platform';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { AccountModule } from '@obo-account/account.module';
import { SharedComponentsModule } from '@obo-common/shared/sharedComponents.module';
import { SharedDirectivesModule } from '@obo-common/shared/sharedDirectives.module';
import { SharedPipesModule } from '@obo-common/shared/sharedPipes.module';
import { DashboardModule } from '@obo-dashboard/dashboard.module';
import { GuidedTourModule } from '@obo-libraries/ngx-guided-tour/guided-tour.module';
import { GuidedTourService } from '@obo-libraries/ngx-guided-tour/guided-tour.service';
import { AlertComponent } from '@obo-main/components/alert/alert.component';
import { ElbridgeBannerComponent } from '@obo-main/components/elbridgeBanner/elbridgeBanner.component';
import { MotdComponent } from '@obo-main/components/motd/motd.component';
import { NavComponent } from '@obo-main/components/nav/nav.component';
import { ProfileCompletionReminderComponent } from '@obo-main/components/profileCompletionReminder/profileCompletionReminder.component';
import { RegionLanguageSelectorModalComponent } from '@obo-main/components/regionLanguageSelectorModal/regionLanguageSelectorModal.component';
import { RootComponent } from '@obo-main/components/root/root.component';
import { SpinnerComponent } from '@obo-main/components/spinner/spinner.component';
import {
    overlayConfigFactory,
    overlayIncendiaryInfoFactory,
    overlayLFactory,
    overlayQuantityCombinationFactory,
    overlayRegionLanguageSelectionFactory,
    overlaySFactory,
    overlayXLFactory,
    overlayDefaultFactory,
    zoomedImageModalFactory,
    overlayFullScreenFactory
} from '@obo-main/factories/OverlayConfig.factory';
import { MAIN_STATES } from '@obo-main/main.states';
import { ServicesModule } from '@obo-main/services/services.module';
import { StaticModule } from '@obo-static/static.module';
import { UIRouterModule } from '@uirouter/angular';
import { NgxPageScrollModule } from 'ngx-page-scroll';
import { DebugOverlayComponent } from './components/debugOverlay/debugOverlay.component';
import { TourComponent } from './components/tour/tour.component';
import {
    OVERLAY_INCENDIARY_INFO_CONFIG,
    OVERLAY_L_CONFIG,
    OVERLAY_QUANTITYCOMBINATION_CONFIG,
    OVERLAY_REGION_LANGUAGE_SELECTION_CONFIG,
    OVERLAY_S_CONFIG,
    OVERLAY_XL_CONFIG,
    OVERLAY_DEFAULT_CONFIG,
    ZOOMED_IMAGE_MODAL_CONFIG,
    OVERLAY_FULL_SCREEN_CONFIG
} from './injectionTokens/overlay.tokens';
import { ChatBotComponent } from '@obo-main/components/chatBot/chat-bot.component';
import { SidebarComponent } from '@obo-common/shared/components/sidebar/sidebar.component';
import { ConfiguratorActionBarComponent } from '@obo-main/components/configurator/configuratorActionBar/configuratorActionBar.component';
import { ConfiguratorNavigationComponent } from '@obo-main/components/configurator/configuratorNavigation/configuratorNavigation.component';
import { ConfiguratorReviewComponent } from '@obo-main/components/configurator/configuratorReview/configuratorReview.component';
import { ConfigurationReviewStepDirective } from '@obo-main/components/configurator/configuratorReview/configuratorReviewStep.directive';
import { RestoreConfigurationComponent } from '@obo-main/components/configurator/restoreConfiguration/restoreConfiguration.component';
import {
    ChatbotOpenButtonComponent
} from '@obo-main/components/chatBot/components/chatbot-open-button/chatbot-open-button.component';

/**
 * App's Main Module. Only purpose is to do the bootstrapping. Also used as Container for all other modules
 */
@NgModule({
    declarations: [
        RootComponent,
        NavComponent,
        AlertComponent,
        SpinnerComponent,
        MotdComponent,
        RegionLanguageSelectorModalComponent,
        ElbridgeBannerComponent,
        ProfileCompletionReminderComponent,
        TourComponent,
        DebugOverlayComponent,
        ConfiguratorActionBarComponent,
        ConfiguratorNavigationComponent,
        ConfiguratorReviewComponent,
        ConfigurationReviewStepDirective,
        RestoreConfigurationComponent
    ],
    imports: [
        //Librarys
        CommonModule,
        FormsModule,
        TranslateModule,
        UIRouterModule.forChild({ states: MAIN_STATES }),
        NgbModule,
        ReactiveFormsModule,
        NgxPageScrollModule,
        OverlayModule,
        FontAwesomeModule,
        GuidedTourModule,
        // self created Modules
        ServicesModule,
        SharedDirectivesModule,
        SharedComponentsModule,
        SharedPipesModule,
        AccountModule,
        StaticModule,
        DashboardModule,
        PlatformModule,
        ChatBotComponent,
        PlatformModule,
        SidebarComponent,
        NgOptimizedImage,
        ChatbotOpenButtonComponent
    ],
    exports: [ConfigurationReviewStepDirective, ConfiguratorReviewComponent, RestoreConfigurationComponent],
    providers: [
        {
            provide: OverlayConfig,
            useFactory: overlayConfigFactory,
            deps: [OverlayPositionBuilder, ScrollStrategyOptions]
        },
        {
            provide: OVERLAY_DEFAULT_CONFIG,
            useFactory: overlayDefaultFactory,
            deps: [OverlayPositionBuilder, ScrollStrategyOptions]
        },
        {
            provide: ZOOMED_IMAGE_MODAL_CONFIG,
            useFactory: zoomedImageModalFactory,
            deps: [OverlayPositionBuilder, ScrollStrategyOptions]
        },
        {
            provide: OVERLAY_S_CONFIG,
            useFactory: overlaySFactory,
            deps: [OverlayPositionBuilder, ScrollStrategyOptions]
        },
        {
            provide: OVERLAY_L_CONFIG,
            useFactory: overlayLFactory,
            deps: [OverlayPositionBuilder, ScrollStrategyOptions]
        },
        {
            provide: OVERLAY_XL_CONFIG,
            useFactory: overlayXLFactory,
            deps: [OverlayPositionBuilder, ScrollStrategyOptions]
        },
        {
            provide: OVERLAY_REGION_LANGUAGE_SELECTION_CONFIG,
            useFactory: overlayRegionLanguageSelectionFactory,
            deps: [OverlayPositionBuilder, ScrollStrategyOptions]
        },
        {
            provide: OVERLAY_FULL_SCREEN_CONFIG,
            useFactory: overlayFullScreenFactory,
            deps: [OverlayPositionBuilder, ScrollStrategyOptions]
        },
        {
            provide: OVERLAY_QUANTITYCOMBINATION_CONFIG,
            useFactory: overlayQuantityCombinationFactory,
            deps: [OverlayPositionBuilder, ScrollStrategyOptions]
        },
        {
            provide: OVERLAY_INCENDIARY_INFO_CONFIG,
            useFactory: overlayIncendiaryInfoFactory,
            deps: [OverlayPositionBuilder, ScrollStrategyOptions]
        },
        GuidedTourService
    ]
})
export class MainModule {}
